// Shared spend-classification for every server-side transaction sum (parity
// P6). The EXCLUDED set used to exist as three separate literals in
// notify-cron.js (the plaid_spend resolver, the monthly rollup, and Kai's
// get_plaid_data tool) — identical members, but a future member added to one
// copy would silently miss the others, and none of the three carried the
// client's legacy-category fallback, so a legacy-shape transfer was excluded
// client-side but counted as spend server-side. One constant, one category
// reader, the way lib/bills is shared.
//
// MATCHED PAIR — `SPEND_EXCLUDED` + `isSpendTx` + `isExcludedFromSpend` in
// src/Scorify.jsx are the client half of this rule (the client cannot import
// api/lib). spendCategory mirrors isSpendTx's reader byte-for-byte in
// semantics: personal_finance_category?.primary || category[0] || "".
// Change the members or the reader on either side and the other must follow.
//
// TRANSFER_OUT IS NOT BLANKET-EXCLUDED (fixed 2026-09-22, device data):
// blanket-excluding primary TRANSFER_OUT dropped real spending — Zelle, Venmo,
// Cash App and Apple Cash payments to PEOPLE land as TRANSFER_OUT
// (detailed TRANSFER_OUT_TRANSFER_OUT_FROM_APPS), and rent paid by
// check/draft and ATM cash land as TRANSFER_OUT_WITHDRAWAL. A real account
// showed August spend of $2,617 when a $2,375 rent check plus cash and a Zelle
// were being silently dropped. Only movements between the user's OWN accounts
// are excluded, identified by the TRANSFER_OUT *detailed* categories below
// (Plaid's own "to another account owned by the holder" bucket). Credit-card
// payments stay excluded via LOAN_PAYMENTS (the card's purchases are already
// counted as spend when the card is connected — the double-count this whole
// exclusion exists to prevent).

// Human-readable category labels, shared with the client (src/lib/spendLabels.js
// is the one home; the client cannot import api/lib, so it imports that module
// directly). DISPLAY ONLY — never used for the spend-inclusion decision below.
export { spendCategoryLabel } from './spendLabels.js';

// Primary categories always excluded from spend: inbound transfers (not an
// outflow) and loan/credit-card payments (double-count guard).
export const SPEND_EXCLUDED = new Set(['TRANSFER_IN', 'LOAN_PAYMENTS']);

// TRANSFER_OUT detailed categories that are movements between the user's OWN
// accounts / into their own savings or investments — not money spent. Every
// OTHER TRANSFER_OUT detailed category (FROM_APPS = P2P to people, WITHDRAWAL =
// cash & checks, OTHER_TRANSFER_OUT) COUNTS as spending.
export const TRANSFER_OUT_SELF_DETAILED = new Set([
  'TRANSFER_OUT_ACCOUNT_TRANSFER',
  'TRANSFER_OUT_INVESTMENT_AND_RETIREMENT_FUNDS',
  'TRANSFER_OUT_SAVINGS',
]);

// The transaction's primary category, with the legacy-shape fallback.
export const spendCategory = (t) =>
  t?.personal_finance_category?.primary || (Array.isArray(t?.category) ? t.category[0] : '') || '';

// THE spend-exclusion decision. Callers still filter amount > 0 and the date
// window themselves; this answers only "is this transaction a non-spend
// transfer/payment?".
export const isExcludedFromSpend = (t) => {
  if (SPEND_EXCLUDED.has(spendCategory(t))) return true;
  const detailed = t?.personal_finance_category?.detailed || '';
  return !!detailed && TRANSFER_OUT_SELF_DETAILED.has(detailed);
};

// ── SPEND BUCKETS (2026-10-13) — LABELING ONLY, never the inclusion decision ──
// The check-in input must say what a day's outflow WAS, not just its sum: a
// rent charge next to an eating-out cap read as "spending jumped" (2 PM test,
// ai_briefings 12f4de73). Buckets for an INCLUDED transaction (isExcludedFromSpend
// false, amount > 0):
//   'bill'      rent, utilities, insurance — obligations, never discretionary
//   'transfer'  money sent to a PERSON (P2P apps, checks, cash) — counts as an
//               outflow but is not a category purchase
//   'everyday'  everything else: the spending a category cap is about
const BILL_PRIMARY = new Set(['RENT_AND_UTILITIES']);
const BILL_DETAILED = new Set(['GENERAL_SERVICES_INSURANCE']);
export const spendBucket = (t) => {
  if (!(t?.amount > 0) || isExcludedFromSpend(t)) return null;
  const primary = spendCategory(t);
  const detailed = t?.personal_finance_category?.detailed || '';
  if (BILL_PRIMARY.has(primary) || BILL_DETAILED.has(detailed)) return 'bill';
  if (primary === 'TRANSFER_OUT') return 'transfer';
  return 'everyday';
};

// THE spend-cap transaction test (moved here from api/lib/goal-progress.js,
// 2026-10-17, so the client's Budget Adherence and the server's month
// resolver and YESTERDAY block share it): a positive (outflow) transaction,
// not an excluded transfer/payment, in the goal's category and, when the
// goal names them, one of its detailed categories.
export function txMatchesSpendGoal(t, config = {}) {
  if (!t || !(Number(t.amount) > 0)) return false; // negative = deposits/refunds/incoming
  if (isExcludedFromSpend(t)) return false;
  const primary = spendCategory(t);
  if (config.category && primary !== config.category) return false;
  if (config.detailed_categories?.length) {
    const detailed = t.personal_finance_category?.detailed;
    if (!config.detailed_categories.includes(detailed)) return false;
  }
  // scope 'everyday' (custom $ caps, 2026-10-20): purchases only, by the same
  // buckets the check-in labels with: rent/utilities/insurance ('bill') and
  // money sent to people ('transfer') are out, unless include_bills brings the
  // bills back in. No scope = the template behavior (every included outflow).
  if (config.scope === 'everyday') {
    const b = spendBucket(t);
    if (!(b === 'everyday' || (config.include_bills && b === 'bill'))) return false;
  }
  return true;
}

// ── TRANSACTION DAY (2026-10-18) ──────────────────────────────────────────────
// THE local day of a transaction, everywhere one is dated (resolvers, the
// YESTERDAY block, notify-cron signals, the day table, the Money tab, Budget
// Adherence). Precedence: authorized_datetime → datetime → authorized_date →
// date. A datetime is an instant and is converted to the user's zone
// (`zone`; undefined = the device's own zone on the client). A date is the
// bank's calendar day and is used as is. A PENDING row usually carries only
// `date`, and banks often stamp it in UTC: a 9 PM Pacific dinner on Oct 16
// arrives as Oct 17 with no clock (the Yard House case). txHasClock tells the
// wording layer whether a day word ("today", "last night") can be trusted.
export function txHasClock(t) {
  return !!(t && (t.authorized_datetime || t.datetime));
}
export function txLocalDay(t, zone) {
  if (!t) return null;
  const iso = t.authorized_datetime || t.datetime || null;
  if (iso) {
    const ms = Date.parse(iso);
    if (Number.isFinite(ms)) {
      try {
        return new Intl.DateTimeFormat('en-CA', zone ? { timeZone: zone } : {}).format(new Date(ms));
      } catch { /* bad zone → fall through to the date fields */ }
    }
  }
  if (typeof t.authorized_date === 'string' && t.authorized_date.length >= 10) return t.authorized_date.slice(0, 10);
  const bank = typeof t.date === 'string' && t.date.length >= 10 ? t.date.slice(0, 10) : null;
  if (!bank) return null;
  // No clock and no authorized date: the bank's date can be the UTC day after
  // an evening purchase. first_seen_at (stamped by the sync the first time any
  // sync saw the transaction_id, carried from pending to posted) bounds it
  // from above: the purchase cannot be later than the day we first saw it.
  // The earlier of the two, never later than the bank date.
  if (t.first_seen_at) {
    const ms = Date.parse(t.first_seen_at);
    if (Number.isFinite(ms)) {
      try {
        const seen = new Intl.DateTimeFormat('en-CA', zone ? { timeZone: zone } : {}).format(new Date(ms));
        return seen < bank ? seen : bank;
      } catch { /* bad zone → bank date */ }
    }
  }
  return bank;
}
/** True when the day comes from a clock or an authorized date, not from the bank's posting/pending date alone. */
export function txDayIsBacked(t) {
  return txHasClock(t) || (typeof t?.authorized_date === 'string' && t.authorized_date.length >= 10);
}
/** Local hour (0-23) of the transaction's clock, or null without one. */
export function txLocalHour(t, zone) {
  const iso = t?.authorized_datetime || t?.datetime || null;
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-US', { ...(zone ? { timeZone: zone } : {}), hour: 'numeric', hour12: false }).formatToParts(new Date(ms));
    return Number(parts.find(p => p.type === 'hour')?.value) % 24;
  } catch { return null; }
}
const WEEKDAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayDiff = (from, to) => Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86400000);
/**
 * How to SAY when a transaction happened, relative to `today` (local).
 * With a clock: "today", "last night" (yesterday, 6 PM or later), "yesterday",
 * a weekday within the last week, else "on Oct 16". With an authorized date
 * but no clock: the same minus "last night". With NEITHER (the bank's
 * posting/pending date alone, bounded by first_seen_at): NO day word at all
 * when the estimated day is today or yesterday ("the Yard House charge"),
 * because the bank's date is routinely the UTC day after an evening
 * purchase; two or more days back a weekday is fine, a week or more the date.
 */
export function txDayWord(t, today, zone) {
  const day = txLocalDay(t, zone);
  if (!day || !today) return '';
  const diff = dayDiff(day, today);
  const label = () => { const d = new Date(`${day}T12:00:00Z`); return `${MON[d.getUTCMonth()]} ${d.getUTCDate()}`; };
  const weekday = () => WEEKDAY[new Date(`${day}T12:00:00Z`).getUTCDay()];
  if (txHasClock(t)) {
    if (diff === 0) return 'today';
    if (diff === 1) { const h = txLocalHour(t, zone); return h != null && h >= 18 ? 'last night' : 'yesterday'; }
    if (diff > 1 && diff < 7) return `on ${weekday()}`;
    return `on ${label()}`;
  }
  if (!txDayIsBacked(t) && diff >= 0 && diff < 2) return '';
  if (diff === 0) return 'today';
  if (diff === 1) return 'yesterday';
  if (diff > 1 && diff < 7) return `on ${weekday()}`;
  return `on ${label()}`;
}
